import {
	Briefcase,
	CheckCircle2,
	FileText,
	Gavel,
	Layers,
	PenLine,
	Search,
	Share2,
	ShieldCheck,
	Users,
	Workflow,
} from 'lucide-react'
import { type LoaderFunctionArgs, type MetaFunction, Link, redirect } from 'react-router'
import { ScrollReveal } from '#app/components/scroll-reveal.tsx'
import { SmoothScroller } from '#app/components/smooth-scroller.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { FAQ } from './components/faq.tsx'
import { MarketingButton } from './components/marketing-button.tsx'
import { PlansComponent } from './components/plans.tsx'
import { Testimonials } from './components/testimonials.tsx'
import { Title } from './components/title.tsx'

export async function loader({ request }: LoaderFunctionArgs) {
	let userId = await getUserId(request)
	if (userId) {
		throw redirect('/ai')
	}
	return {}
}

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
	let title = loaderData ? 'IUS PRO | IA legal para abogados y firmas en Costa Rica' : 'Error | IUS PRO'
	if (process.env.NODE_ENV === 'development') {
		title = '(dev) ' + title
	}
	return [
		{ title },
		{
			name: 'description',
			content:
				'La plataforma de IA legal mas precisa para Costa Rica. Debida diligencia, revision de contratos, expedientes, cumplimiento normativo y revision documental masiva.',
		},
		{
			name: 'og:title',
			content: 'IUS PRO | IA legal para abogados y firmas en Costa Rica',
		},
		{
			name: 'og:description',
			content:
				'Automatice investigacion, revision documental y analisis juridico con IA especializada en normativa, jurisprudencia y practica costarricense.',
		},
		{ name: 'og:image', content: 'https://iuspro.com/img/og-image.jpeg' },
	]
}

export default function Index() {
	return (
		<>
			<SmoothScroller />
			<HeroSection />
			<FeaturesOverview />
			<PrecisionSection />
			<SmartLawSection />
			<DocumentProcessingSection />
			<SmartDraftingSection />
			<IndividualLawyersSection />
			<FirmsSection />
			<SecuritySection />
			<Testimonials />
			<PlansComponent />
			<FAQ />
			<FinalSection />
		</>
	)
}

const colors = {
	bg: '#f8f9fc',
	gridColor: '#1a274450',
	waveColors: {
		start: '#1a2744',
		mid1: '#2d3f5f',
		mid2: '#c9a962',
		mid3: '#e8d5a3',
		end: '#0f1829',
	},
	accent: '#c9a962',
	primary: '#0f1829',
	secondary: '#1a2744',
	textDark: '#0f1829',
	textMuted: '#4d5f7f',
	textNav: '#3d4f6f',
	lightBg: '#f0f4fc',
}

function WaveStyleSCurve({ gradientId }: { gradientId: string }) {
	return (
		<svg
			viewBox="0 0 1400 900"
			className="absolute -left-[15%] -top-[15%] h-[130%] w-[130%]"
			preserveAspectRatio="xMidYMid slice"
		>
			<defs>
				<linearGradient id={gradientId} x1="0%" y1="100%" x2="100%" y2="0%">
					<stop offset="0%" stopColor={colors.waveColors.start}>
						<animate
							attributeName="stop-color"
							values={`${colors.waveColors.start};${colors.waveColors.mid2};${colors.waveColors.start}`}
							dur="10s"
							repeatCount="indefinite"
						/>
					</stop>
					<stop offset="50%" stopColor={colors.waveColors.mid2}>
						<animate
							attributeName="stop-color"
							values={`${colors.waveColors.mid2};${colors.waveColors.mid3};${colors.waveColors.mid2}`}
							dur="8s"
							repeatCount="indefinite"
						/>
					</stop>
					<stop offset="100%" stopColor={colors.waveColors.end}>
						<animate
							attributeName="stop-color"
							values={`${colors.waveColors.end};${colors.waveColors.mid1};${colors.waveColors.end}`}
							dur="12s"
							repeatCount="indefinite"
						/>
					</stop>
				</linearGradient>
				<filter id={`${gradientId}-glow`} x="-50%" y="-50%" width="200%" height="200%">
					<feGaussianBlur stdDeviation="12" result="coloredBlur" />
					<feMerge>
						<feMergeNode in="coloredBlur" />
						<feMergeNode in="SourceGraphic" />
					</feMerge>
				</filter>
			</defs>

			<path
				d="M-200,700 C100,750 200,400 500,350 C800,300 900,550 1200,500 C1500,450 1600,200 1700,150"
				fill="none"
				stroke={`url(#${gradientId})`}
				strokeWidth="120"
				strokeLinecap="round"
				filter={`url(#${gradientId}-glow)`}
				opacity="0.85"
			>
				<animate
					attributeName="d"
					dur="18s"
					repeatCount="indefinite"
					values="
            M-200,700 C100,750 200,400 500,350 C800,300 900,550 1200,500 C1500,450 1600,200 1700,150;
            M-200,650 C100,700 200,350 500,300 C800,250 900,500 1200,450 C1500,400 1600,150 1700,100;
            M-200,720 C100,770 200,420 500,370 C800,320 900,570 1200,520 C1500,470 1600,220 1700,170;
            M-200,700 C100,750 200,400 500,350 C800,300 900,550 1200,500 C1500,450 1600,200 1700,150
          "
				/>
			</path>

			<path
				d="M-150,750 C150,800 250,450 550,400 C850,350 950,600 1250,550 C1550,500 1650,250 1750,200"
				fill="none"
				stroke={colors.waveColors.mid3}
				strokeWidth="40"
				strokeLinecap="round"
				opacity="0.4"
			>
				<animate
					attributeName="d"
					dur="15s"
					repeatCount="indefinite"
					values="
            M-150,750 C150,800 250,450 550,400 C850,350 950,600 1250,550 C1550,500 1650,250 1750,200;
            M-150,700 C150,750 250,400 550,350 C850,300 950,550 1250,500 C1550,450 1650,200 1750,150;
            M-150,750 C150,800 250,450 550,400 C850,350 950,600 1250,550 C1550,500 1650,250 1750,200
          "
				/>
			</path>

			<path
				d="M-100,800 C200,850 300,500 600,450 C900,400 1000,650 1300,600"
				fill="none"
				stroke={colors.waveColors.mid2}
				strokeWidth="4"
				strokeLinecap="round"
				opacity="0.5"
			/>
		</svg>
	)
}

function HeroSection() {
	const user = useOptionalUser()
	return (
		<section className="relative h-[100dvh] overflow-hidden bg-[#fefefe]">
			{/* Subtle grid pattern */}
			<div
				className="absolute inset-0 opacity-[0.12]"
				style={{
					backgroundImage: `linear-gradient(#d5d0c8 1px, transparent 1px), linear-gradient(90deg, #d5d0c8 1px, transparent 1px)`,
					backgroundSize: '80px 80px',
				}}
			/>

			{/* Wave */}
			<div className="absolute inset-0 overflow-hidden">
				<WaveStyleSCurve gradientId="hero-wave" />
			</div>

			{/* Hero content, bottom aligned */}
			<div className="absolute bottom-0 left-0 right-0 z-10 px-6 pb-16 md:px-10 lg:px-16 lg:pb-20">
				<div className="mx-auto max-w-7xl">
					<h1 className="font-regular max-w-4xl text-balance text-4xl font-light leading-[1.1] tracking-[-0.07em] text-neutral-950 md:text-5xl lg:text-6xl xl:text-7xl">
						Donde se hace el trabajo legal.
					</h1>
					<div className="mt-10 flex flex-col gap-6 sm:flex-row sm:items-start lg:mt-12">
						{user ? (
							<MarketingButton variant="primary" size="lg" className="rounded-lg px-6 py-4 text-base" asChild>
								<Link prefetch="render" to="/ai">
									Ir a la aplicación
									<Icon name="arrow-right-thin" className="ml-4 h-5 w-5" />
								</Link>
							</MarketingButton>
						) : (
							<div className="flex flex-col gap-3">
								<div className="flex flex-col gap-4 md:flex-row">
									<MarketingButton asChild variant="primary" className="rounded-none px-6 py-4 text-sm" size="lg">
										<Link to="/links/demo" target="_blank">
											Agendar demostración
										</Link>
									</MarketingButton>
									<MarketingButton
										size="lg"
										className="rounded-none border border-neutral-200 bg-white text-sm font-normal text-foreground shadow-none hover:hover:bg-neutral-100/20"
										asChild
									>
										<Link prefetch="render" to="/signup">
											Iniciar prueba gratis
											<Icon name="arrow-right-thin" className="ml-4 h-5 w-5" />
										</Link>
									</MarketingButton>
								</div>
							</div>
						)}
						<h2 className="max-w-lg text-pretty text-sm leading-relaxed text-neutral-500 lg:text-base">
							Investigue, analice expedientes, redacte, revise en lote y asigne el trabajo a su equipo o a la
							plataforma. Todo dentro del mismo sistema, con citas verificables a normativa y jurisprudencia
							costarricense.
						</h2>
					</div>
				</div>
			</div>
		</section>
	)
}

const overviewOutcomes = [
	{ value: '1M+', label: 'Sentencias y normativa costarricense indexadas' },
	{ value: '100%', label: 'Citas verificables. Cero inventadas.' },
	{ value: '4h → 10min', label: 'De redactar un escrito a solo revisarlo' },
	{
		value: '100s',
		label: 'Documentos revisados a la vez, con un mismo criterio',
	},
]

function FeaturesOverview() {
	return (
		<section className="w-full bg-neutral-50 py-32 md:py-44">
			<div className="mx-auto max-w-7xl px-6 lg:px-8">
				<ScrollReveal className="mx-auto max-w-3xl text-center">
					<p className="text-sm font-medium uppercase tracking-widest text-[#c9a962]">
						Delegue lo que le consume el día
					</p>
					<Title component="h2" className="mt-6 text-5xl md:text-6xl">
						Deje que el agente haga el trabajo pesado.
						<br />
						<span className="text-neutral-300">Usted revisa y decide.</span>
					</Title>
					<p className="mx-auto mt-8 max-w-xl text-[17px] leading-relaxed text-neutral-500">
						Asígnele las tareas que le roban horas: investigar, leer expedientes completos, redactar y revisar documento
						por documento. Reciba el trabajo hecho, con la fuente original a la vista.
					</p>
				</ScrollReveal>

				{/* Outcome band */}
				<ScrollReveal className="mt-16 overflow-hidden rounded-3xl border border-neutral-200 bg-neutral-200">
					<div className="grid gap-px sm:grid-cols-2 lg:grid-cols-4">
						{overviewOutcomes.map(outcome => (
							<div key={outcome.value} className="bg-white p-8 lg:p-10">
								<Title component="span" className="block text-4xl text-neutral-950 md:text-[2.75rem]">
									{outcome.value}
								</Title>
								<p className="mt-3 text-[14px] leading-relaxed text-neutral-500">{outcome.label}</p>
							</div>
						))}
					</div>
				</ScrollReveal>

				{/* Bento */}
				<div className="mt-8 grid gap-8 lg:grid-cols-5">
					{/* Research flagship */}
					<ScrollReveal className="flex flex-col overflow-hidden rounded-3xl border border-neutral-200 bg-white lg:col-span-3">
						<div className="p-10 lg:p-14 lg:pb-10">
							<Search strokeWidth={1.5} className="h-6 w-6 text-[#c9a962]" />
							<Title component="h3" className="mt-8 text-3xl md:text-4xl">
								Pregunte como le preguntaría a un colega
							</Title>
							<p className="mt-4 max-w-lg text-[16px] leading-relaxed text-neutral-500">
								Reciba la respuesta con el artículo, el voto y el enlace a la fuente original. Sin buscar a mano, sin
								citas que no existen.
							</p>
						</div>

						{/* Answer mock */}
						<div className="mt-auto px-10 pb-10 lg:px-14 lg:pb-14">
							<div className="rounded-2xl bg-neutral-50 p-6 lg:p-8">
								<p className="text-sm font-medium text-neutral-900">
									¿Cuándo prescribe la acción por despido injustificado?
								</p>
								<p className="mt-4 text-sm leading-relaxed text-neutral-600">
									El plazo es de un año contado a partir de la terminación del contrato, conforme al régimen de
									prescripción laboral vigente.
								</p>
								<div className="mt-6 space-y-2">
									{[
										{ source: 'Código de Trabajo', ref: 'Artículo 413' },
										{ source: 'Sala Segunda', ref: 'Criterio reiterado' },
									].map(cite => (
										<div key={cite.ref} className="flex items-center gap-3 rounded-xl bg-white p-3">
											<CheckCircle2 strokeWidth={1.5} className="h-4 w-4 shrink-0 text-[#c9a962]" />
											<span className="flex-1 text-sm text-neutral-700">{cite.source}</span>
											<span className="font-mono text-xs text-neutral-400">{cite.ref}</span>
										</div>
									))}
								</div>
								<p className="mt-5 text-xs text-neutral-400">
									Cada cita abre la fuente original para que usted verifique.
								</p>
							</div>
						</div>
					</ScrollReveal>

					{/* Batch review */}
					<ScrollReveal
						className="flex flex-col overflow-hidden rounded-3xl border border-neutral-200 bg-white lg:col-span-2"
						delay={100}
					>
						<div className="p-10 lg:p-12 lg:pb-8">
							<Briefcase strokeWidth={1.5} className="h-6 w-6 text-[#c9a962]" />
							<Title component="h3" className="mt-8 text-3xl">
								Semanas de revisión, resueltas en horas
							</Title>
							<p className="mt-4 text-[15px] leading-relaxed text-neutral-500">
								Debida diligencia, contratos y cumplimiento: cientos de documentos revisados a la vez con el mismo
								criterio.
							</p>
						</div>

						{/* Review table mock */}
						<div className="mt-auto px-10 pb-10 lg:px-12 lg:pb-12">
							<div className="rounded-2xl bg-neutral-50 p-6">
								<div className="flex items-center justify-between border-b border-neutral-200 pb-3">
									<span className="text-xs font-medium text-neutral-900">Tabla de revisión</span>
									<span className="rounded-full bg-neutral-200 px-2.5 py-1 text-[11px] text-neutral-600">248 docs</span>
								</div>
								<div className="mt-3 space-y-2">
									{[
										{
											doc: 'Arrendamiento 04',
											flag: 'Sin cláusula de rescisión',
											tone: 'risk',
										},
										{
											doc: 'Servicios 17',
											flag: 'Vence en 30 días',
											tone: 'warn',
										},
										{
											doc: 'Confidencialidad 22',
											flag: 'Conforme',
											tone: 'ok',
										},
									].map(row => (
										<div key={row.doc} className="flex items-center gap-3 rounded-xl bg-white p-3">
											<FileText strokeWidth={1.5} className="h-4 w-4 shrink-0 text-neutral-400" />
											<span className="flex-1 truncate text-xs text-neutral-700">{row.doc}</span>
											<span
												className={cn(
													'shrink-0 rounded-full px-2.5 py-1 text-[11px]',
													row.tone === 'risk' && 'bg-red-50 text-red-600',
													row.tone === 'warn' && 'bg-amber-50 text-amber-700',
													row.tone === 'ok' && 'bg-emerald-50 text-emerald-700',
												)}
											>
												{row.flag}
											</span>
										</div>
									))}
								</div>
							</div>
						</div>
					</ScrollReveal>
				</div>

				{/* Supporting trio */}
				<div className="mt-8 grid gap-8 md:grid-cols-3">
					<ScrollReveal className="flex flex-col rounded-3xl border border-neutral-200 bg-white p-10 lg:p-12">
						<Gavel strokeWidth={1.5} className="h-6 w-6 text-[#c9a962]" />
						<h3 className="mt-8 text-xl font-medium tracking-tight text-neutral-950">
							El expediente completo, entendido
						</h3>
						<p className="mt-3 text-[15px] leading-relaxed text-neutral-500">
							Suba el expediente y reciba cronología, riesgos, fortalezas y la jurisprudencia que se parece a su caso.
						</p>
						<div className="mt-8 flex-1 space-y-3 border-t border-neutral-100 pt-8 text-[14px] leading-relaxed text-neutral-400">
							<p>Comparación contra cientos de casos similares</p>
							<p>Integrado con el chat y la redacción del mismo caso</p>
						</div>
					</ScrollReveal>

					<ScrollReveal
						className="flex flex-col rounded-3xl border border-neutral-200 bg-white p-10 lg:p-12"
						delay={100}
					>
						<PenLine strokeWidth={1.5} className="h-6 w-6 text-[#c9a962]" />
						<h3 className="mt-8 text-xl font-medium tracking-tight text-neutral-950">Redacción en su estilo</h3>
						<p className="mt-3 text-[15px] leading-relaxed text-neutral-500">
							Escritos con la estructura correcta y las citas exactas de su caso, listos para revisar y firmar.
						</p>
					</ScrollReveal>

					<ScrollReveal
						className="flex flex-col rounded-3xl border border-neutral-200 bg-white p-10 lg:p-12"
						delay={200}
					>
						<Workflow strokeWidth={1.5} className="h-6 w-6 text-[#c9a962]" />
						<h3 className="mt-8 text-xl font-medium tracking-tight text-neutral-950">El criterio de su despacho</h3>
						<p className="mt-3 text-[15px] leading-relaxed text-neutral-500">
							Convierta su forma de trabajar en agentes y plantillas que todo el equipo reutiliza.
						</p>
					</ScrollReveal>
				</div>

				<ScrollReveal className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm font-medium">
					<Link
						prefetch="intent"
						to="/despachos"
						className="text-neutral-600 underline underline-offset-4 transition-colors hover:text-neutral-950"
					>
						IUS PRO para despachos
					</Link>
					<Link
						prefetch="intent"
						to="/producto/seguridad"
						className="text-neutral-600 underline underline-offset-4 transition-colors hover:text-neutral-950"
					>
						Seguridad para equipos legales
					</Link>
				</ScrollReveal>
			</div>
		</section>
	)
}

function PrecisionSection() {
	return (
		<section className="w-full bg-neutral-950 py-32 text-white md:py-40">
			<div className="mx-auto max-w-7xl px-6 lg:px-8">
				<ScrollReveal className="mx-auto max-w-3xl text-center">
					<p className="text-sm font-medium uppercase tracking-widest text-[#c9a962]">Precisión verificable</p>
					<Title component="h2" className="mt-6 text-4xl text-white md:text-5xl">
						Precisión que se puede verificar, cita por cita.
					</Title>
					<p className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-neutral-400">
						En derecho, una cita inventada puede costar un caso. Hoy muchas herramientas intentan usar IA; IUS PRO va
						varias millas más allá: creamos cientos de benchmarks propios para garantizar la precisión jurídica de cada
						respuesta. Es la razón por la que muchos bufetes nos eligieron.
					</p>
				</ScrollReveal>

				<div className="mt-20 grid items-stretch gap-8 lg:grid-cols-2">
					{/* The number */}
					<ScrollReveal className="flex flex-col justify-center rounded-3xl border border-neutral-800 bg-neutral-900/40 p-12 text-center lg:p-16">
						<Title component="span" className="text-7xl text-[#c9a962] md:text-8xl">
							100%
						</Title>
						<p className="mt-6 text-lg font-medium text-white">Citas verificables. Cero inventadas.</p>
						<p className="mx-auto mt-3 max-w-xs text-[15px] leading-relaxed text-neutral-400">
							Cada cita legal que produce el sistema apunta a una fuente real y correcta, lista para que usted la
							revise.
						</p>
					</ScrollReveal>

					{/* The methodology */}
					<ScrollReveal className="rounded-3xl border border-neutral-800 bg-neutral-900/40 p-12 lg:p-16" delay={100}>
						<h3 className="text-sm font-medium uppercase tracking-widest text-neutral-500">Cómo lo medimos</h3>
						<ul className="mt-8 space-y-7">
							<li className="flex gap-4">
								<CheckCircle2 strokeWidth={1.5} className="mt-0.5 h-5 w-5 shrink-0 text-[#c9a962]" />
								<div>
									<p className="font-medium text-white">Qué medimos</p>
									<p className="mt-1 text-[15px] leading-relaxed text-neutral-400">
										Que cada cita legal que el sistema produce apunte a una fuente real y correcta, no a una inventada.
									</p>
								</div>
							</li>
							<li className="flex gap-4">
								<CheckCircle2 strokeWidth={1.5} className="mt-0.5 h-5 w-5 shrink-0 text-[#c9a962]" />
								<div>
									<p className="font-medium text-white">Cómo</p>
									<p className="mt-1 text-[15px] leading-relaxed text-neutral-400">
										Cientos de benchmarks propios con consultas jurídicas reales, cada respuesta verificada contra su
										fuente original.
									</p>
								</div>
							</li>
							<li className="flex gap-4">
								<CheckCircle2 strokeWidth={1.5} className="mt-0.5 h-5 w-5 shrink-0 text-[#c9a962]" />
								<div>
									<p className="font-medium text-white">Resultado</p>
									<p className="mt-1 text-[15px] leading-relaxed text-neutral-400">
										100% de citas verificables y cero alucinaciones en las citas.
									</p>
								</div>
							</li>
						</ul>
					</ScrollReveal>
				</div>

				<ScrollReveal className="mt-8 flex flex-col items-center justify-between gap-3 rounded-3xl border border-neutral-800 bg-neutral-900/40 px-10 py-8 text-center sm:flex-row sm:text-left">
					<p className="text-[15px] leading-relaxed text-neutral-400">
						La mayoría de las herramientas de IA legal no publican su tasa de precisión.
					</p>
					<p className="shrink-0 text-lg font-medium text-[#c9a962]">Nosotros sí.</p>
				</ScrollReveal>
			</div>
		</section>
	)
}

function SmartLawSection() {
	return (
		<section className="w-full bg-[#fbfaf7] py-28 md:py-36">
			<div className="mx-auto max-w-7xl px-6 lg:px-8">
				<ScrollReveal className="grid gap-8 border-t border-neutral-300 pt-6 lg:grid-cols-12 lg:gap-12">
					<div className="flex items-center gap-3 lg:col-span-3 lg:items-start">
						<span className="font-mono text-xs text-[#9b803e]">01</span>
						<p className="text-xs font-medium uppercase tracking-[0.2em] text-neutral-500">
							Inteligencia jurídica local
						</p>
					</div>
					<div className="lg:col-span-9">
						<Title component="h2" className="max-w-4xl text-4xl md:text-5xl lg:text-6xl">
							La diferencia no es solo lo que hace.
							<span className="block text-neutral-400">Es lo que entiende.</span>
						</Title>
						<p className="mt-7 max-w-2xl text-base leading-relaxed text-neutral-600 md:text-lg">
							Derecho, fuentes y práctica jurídica costarricense, dentro del mismo flujo de trabajo.
						</p>
					</div>
				</ScrollReveal>

				<div className="mt-20 grid items-center gap-12 lg:mt-24 lg:grid-cols-12 lg:gap-16">
					<ScrollReveal className="lg:col-span-5">
						<Title component="h3" className="max-w-md text-3xl md:text-4xl">
							Cada cita apunta a una fuente real
						</Title>
						<p className="mt-6 max-w-lg text-[17px] leading-relaxed text-neutral-600">
							En derecho, una cita inventada puede costar un caso. IUS PRO razona sobre más de un millón de sentencias y
							la normativa vigente, y acompaña cada respuesta con su fuente original para que usted la abra y la
							verifique.
						</p>
						<div className="mt-9 flex items-center gap-4 border-t border-neutral-300 pt-5">
							<CheckCircle2 strokeWidth={1.5} className="h-5 w-5 shrink-0 text-[#9b803e]" />
							<p className="text-sm leading-relaxed text-neutral-600">La respuesta y su respaldo, siempre juntos.</p>
						</div>
					</ScrollReveal>

					<ScrollReveal className="relative lg:col-span-7" delay={100}>
						<div className="overflow-hidden rounded-[1.75rem] bg-neutral-200">
							<img
								src="/img/marketing/research.webp"
								alt="Mesa de lectura de una biblioteca jurídica con lámpara de bronce y volúmenes encuadernados"
								width={1440}
								height={1080}
								loading="lazy"
								className="aspect-[4/3] w-full object-cover"
							/>
						</div>
						<div className="relative -mt-8 ml-5 max-w-sm rounded-2xl border border-neutral-200 bg-white p-5 shadow-[0_18px_45px_-24px_rgba(15,15,15,0.35)] sm:ml-8 sm:p-6 lg:absolute lg:-bottom-8 lg:left-8 lg:ml-0">
							<div className="flex items-start justify-between gap-5">
								<div>
									<p className="text-[11px] font-medium uppercase tracking-[0.18em] text-neutral-400">
										Fuente original
									</p>
									<p className="mt-2 text-sm font-medium text-neutral-900">Normativa y jurisprudencia costarricense</p>
								</div>
								<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f4efe3] text-[#8b7338]">
									<CheckCircle2 aria-hidden="true" strokeWidth={1.7} className="h-4 w-4" />
								</span>
							</div>
							<p className="mt-4 border-t border-neutral-100 pt-4 text-xs leading-relaxed text-neutral-500">
								Lista para abrir, leer y comprobar.
							</p>
						</div>
					</ScrollReveal>
				</div>

				<div className="mt-24 grid border-y border-neutral-300 md:mt-32 md:grid-cols-3">
					<ScrollReveal className="py-9 md:pr-8 lg:py-11 lg:pr-12">
						<div className="flex items-baseline justify-between gap-6">
							<Title component="span" className="text-5xl md:text-6xl">
								1M+
							</Title>
							<span className="font-mono text-xs text-neutral-400">01</span>
						</div>
						<h3 className="mt-5 text-lg font-medium tracking-tight text-neutral-900">Sentencias indexadas</h3>
						<p className="mt-3 text-sm leading-relaxed text-neutral-500">
							Sala Primera, Sala Constitucional, tribunales y criterios locales.
						</p>
					</ScrollReveal>

					<ScrollReveal
						className="border-t border-neutral-300 py-9 md:border-l md:border-t-0 md:px-8 lg:px-12 lg:py-11"
						delay={100}
					>
						<div className="flex items-baseline justify-between gap-6">
							<p className="text-xs font-medium uppercase tracking-[0.18em] text-[#9b803e]">Cobertura local</p>
							<span className="font-mono text-xs text-neutral-400">02</span>
						</div>
						<h3 className="mt-5 text-xl font-medium tracking-tight text-neutral-900">Fuentes locales, no genéricas</h3>
						<p className="mt-3 text-sm leading-relaxed text-neutral-500">
							Normativa vigente, La Gaceta, Boletín Judicial y documentos legales de Costa Rica.
						</p>
					</ScrollReveal>

					<ScrollReveal
						className="border-t border-neutral-300 py-9 md:border-l md:border-t-0 md:pl-8 lg:py-11 lg:pl-12"
						delay={200}
					>
						<div className="flex items-baseline justify-between gap-6">
							<p className="text-xs font-medium uppercase tracking-[0.18em] text-[#9b803e]">A cualquier escala</p>
							<span className="font-mono text-xs text-neutral-400">03</span>
						</div>
						<h3 className="mt-5 text-xl font-medium tracking-tight text-neutral-900">
							Un caso o cientos de documentos
						</h3>
						<p className="mt-3 text-sm leading-relaxed text-neutral-500">
							Investigación, debida diligencia, auditorías, cumplimiento, expedientes y redacción.
						</p>
					</ScrollReveal>
				</div>
			</div>
		</section>
	)
}

function DocumentProcessingSection() {
	return (
		<section className="w-full bg-neutral-50 py-32 md:py-40">
			<div className="mx-auto max-w-7xl px-6 lg:px-8">
				<div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-24">
					<ScrollReveal className="space-y-12">
						<div className="space-y-6">
							<p className="text-sm font-medium uppercase tracking-widest text-[#c9a962]">
								Comparación de casos similares
							</p>
							<Title component="h2" className="text-4xl md:text-5xl">
								Compare su caso contra cientos de casos similares.
							</Title>
							<p className="text-lg leading-relaxed text-neutral-600">
								La única IA jurídica en Costa Rica que compara su expediente con cientos de casos similares para
								identificar tendencias, argumentos ganadores y jurisprudencia relevante.
							</p>
						</div>

						<ul className="space-y-8">
							<li className="flex items-start gap-4">
								<span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400" />
								<div>
									<h4 className="text-lg font-medium text-neutral-900">Tendencias jurisprudenciales</h4>
									<p className="mt-1 text-base leading-relaxed text-neutral-600">
										Hacia dónde se inclina el criterio de los tribunales en casos como el suyo.
									</p>
								</div>
							</li>
							<li className="flex items-start gap-4">
								<span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400" />
								<div>
									<h4 className="text-lg font-medium text-neutral-900">Argumentos que tuvieron éxito</h4>
									<p className="mt-1 text-base leading-relaxed text-neutral-600">
										Las líneas de argumentación que prosperaron en casos similares.
									</p>
								</div>
							</li>
							<li className="flex items-start gap-4">
								<span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400" />
								<div>
									<h4 className="text-lg font-medium text-neutral-900">Argumentos rechazados por los tribunales</h4>
									<p className="mt-1 text-base leading-relaxed text-neutral-600">
										Los planteamientos que fueron desestimados, para anticiparlos.
									</p>
								</div>
							</li>
							<li className="flex items-start gap-4">
								<span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400" />
								<div>
									<h4 className="text-lg font-medium text-neutral-900">Factores asociados a resultados favorables</h4>
									<p className="mt-1 text-base leading-relaxed text-neutral-600">
										Los elementos que suelen acompañar a las resoluciones a favor.
									</p>
								</div>
							</li>
							<li className="flex items-start gap-4">
								<span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400" />
								<div>
									<h4 className="text-lg font-medium text-neutral-900">Criterios que han cambiado con el tiempo</h4>
									<p className="mt-1 text-base leading-relaxed text-neutral-600">
										Cómo ha evolucionado la posición de los tribunales sobre la materia.
									</p>
								</div>
							</li>
						</ul>
					</ScrollReveal>

					{/* Graphic Representation */}
					<ScrollReveal className="relative" delay={200}>
						<div className="overflow-hidden rounded-3xl bg-white p-2 shadow-2xl shadow-neutral-900/5">
							<div className="space-y-6 rounded-2xl bg-neutral-50 p-8">
								<div className="flex items-center justify-between border-b border-neutral-200 pb-4">
									<span className="text-sm font-medium text-neutral-900">Expediente #23-000456-1203-CJ</span>
									<span className="rounded-full bg-neutral-200 px-3 py-1 text-xs text-neutral-600">Comparado</span>
								</div>

								<div className="space-y-4">
									<div className="inline-flex w-full items-center gap-2 rounded-xl bg-neutral-100 px-3 py-2 text-xs text-neutral-700">
										<CheckCircle2 className="h-3.5 w-3.5" />
										Comparado contra 187 casos similares
									</div>
									<div className="rounded-xl bg-white p-5">
										<p className="mb-2 text-xs text-neutral-500">Tendencia jurisprudencial</p>
										<p className="text-sm leading-relaxed text-neutral-800">
											El 71% de los casos con prueba de fecha cierta se resolvió a favor del actor.
										</p>
									</div>

									<div className="rounded-xl bg-white p-5">
										<p className="mb-2 text-xs text-neutral-500">Argumento con mayor éxito</p>
										<p className="text-sm leading-relaxed text-neutral-800">
											La excepción de prescripción parcial prosperó en la mayoría de casos análogos.
										</p>
									</div>
								</div>

								<button className="w-full rounded-xl bg-neutral-900 py-3.5 text-sm font-medium text-white">
									Ver comparación completa
								</button>
							</div>
						</div>
					</ScrollReveal>
				</div>
			</div>
		</section>
	)
}

function FinalSection() {
	return (
		<section className="w-full bg-neutral-950 py-32 text-white md:py-40">
			<div className="mx-auto max-w-7xl space-y-12 px-6 text-center lg:px-8">
				<ScrollReveal>
					<Title component="h2" className="text-5xl text-white md:text-6xl">
						Una plataforma legal completa, especializada en Costa Rica.
					</Title>
				</ScrollReveal>
				<ScrollReveal delay={100}>
					<p className="mx-auto max-w-2xl text-xl leading-relaxed tracking-tight text-neutral-400">
						Automatice investigación, revisión documental, expedientes y flujos jurídicos con fuentes costarricenses
						verificables.
					</p>
				</ScrollReveal>
				<ScrollReveal className="flex flex-col justify-center gap-4 sm:flex-row" delay={200}>
					<MarketingButton variant="inverted" className="px-10" asChild>
						<Link prefetch="render" to="/signup">
							Comenzar prueba gratuita
						</Link>
					</MarketingButton>
					<MarketingButton variant="inverted-outline" className="px-10" asChild>
						<Link prefetch="render" to="/links/demo" target="_blank">
							Ver demostración en vivo
						</Link>
					</MarketingButton>
				</ScrollReveal>
				<ScrollReveal delay={300}>
					<p className="text-sm tracking-tight text-neutral-500">
						No requiere tarjeta de crédito para empezar.{' '}
						<Link
							prefetch="render"
							to="/login"
							className="font-medium text-neutral-300 underline underline-offset-4 transition-colors hover:text-white"
						>
							Iniciar sesión
						</Link>
					</p>
				</ScrollReveal>
			</div>
		</section>
	)
}

function SmartDraftingSection() {
	return (
		<section className="w-full bg-white py-32 md:py-40">
			<div className="mx-auto max-w-7xl space-y-24 px-6 lg:px-8">
				{/* Header */}
				<ScrollReveal className="mx-auto max-w-3xl text-center">
					<p className="text-sm font-medium uppercase tracking-widest text-[#c9a962]">Redacción inteligente</p>
					<Title component="h2" className="mt-6 text-4xl md:text-5xl">
						De 4 horas redactando a 10 minutos revisando.
					</Title>
					<p className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-neutral-600">
						Deja de empezar documentos desde cero. IUS PRO genera escritos profesionales en minutos, con tu estilo y las
						citas legales exactas para tu caso.
					</p>
				</ScrollReveal>

				{/* Before / After */}
				<ScrollReveal className="mx-auto flex max-w-xl items-center justify-center gap-8">
					<div className="space-y-2 text-center">
						<p className="text-4xl font-semibold text-neutral-300">4h</p>
						<p className="text-sm tracking-tight text-neutral-500">Redacción manual</p>
					</div>
					<div className="h-1.5 w-1.5 rounded-full bg-[#c9a962]" />
					<div className="space-y-2 text-center">
						<p className="text-4xl font-semibold text-neutral-950">10min</p>
						<p className="text-sm tracking-tight text-neutral-500">Con IUS PRO</p>
					</div>
				</ScrollReveal>

				{/* Steps */}
				<div className="grid gap-8 md:grid-cols-3">
					<ScrollReveal className="flex flex-col rounded-3xl bg-neutral-50 p-10">
						<span className="font-mono text-xs uppercase tracking-widest text-[#c9a962]">Paso 01</span>
						<h3 className="mb-4 mt-4 text-lg font-medium text-neutral-950">Describe qué necesitas</h3>
						<p className="leading-relaxed tracking-tight text-neutral-600">
							"Genera contestación de demanda laboral por despido sin justa causa. Tengo 3 testigos y comprobantes de
							pago."
						</p>
					</ScrollReveal>

					<ScrollReveal className="flex flex-col rounded-3xl bg-neutral-50 p-10" delay={100}>
						<span className="font-mono text-xs uppercase tracking-widest text-[#c9a962]">Paso 02</span>
						<h3 className="mb-4 mt-4 text-lg font-medium text-neutral-950">La IA redacta</h3>
						<p className="leading-relaxed tracking-tight text-neutral-600">
							En 90 segundos: estructura procesal correcta, citas legales aplicables, y secciones completas de hechos,
							derecho y petitoria.
						</p>
					</ScrollReveal>

					<ScrollReveal className="flex flex-col rounded-3xl bg-neutral-50 p-10" delay={200}>
						<span className="font-mono text-xs uppercase tracking-widest text-[#c9a962]">Paso 03</span>
						<h3 className="mb-4 mt-4 text-lg font-medium text-neutral-950">Ajustas y presentas</h3>
						<p className="leading-relaxed tracking-tight text-neutral-600">
							Revisa, personaliza detalles específicos y firma. Tiempo promedio de edición: 10-15 minutos.
						</p>
					</ScrollReveal>
				</div>

				{/* Style Replication */}
				<ScrollReveal className="rounded-3xl border border-neutral-200 bg-neutral-50 p-10">
					<div className="grid items-center gap-12 lg:grid-cols-2">
						<div className="space-y-6">
							<Title component="h3" className="text-3xl md:text-4xl">
								Replica tu estilo automáticamente
							</Title>
							<div className="space-y-4">
								<p className="text-lg tracking-tight text-neutral-600">
									¿Ya tienes un estilo de redacción consolidado?
								</p>
								<ul className="space-y-3 text-neutral-700">
									<li className="flex items-center gap-3">
										<span className="h-1.5 w-1.5 rounded-full bg-[#c9a962]" />
										Sube 2-3 documentos de muestra
									</li>
									<li className="flex items-center gap-3">
										<span className="h-1.5 w-1.5 rounded-full bg-[#c9a962]" />
										IUS PRO aprende tu estructura y tono
									</li>
									<li className="flex items-center gap-3">
										<span className="h-1.5 w-1.5 rounded-full bg-[#c9a962]" />
										Documentos futuros replican tu estilo
									</li>
								</ul>
							</div>
						</div>

						{/* Style mock */}
						<div className="overflow-hidden rounded-3xl bg-white p-2 shadow-2xl shadow-neutral-900/5">
							<div className="space-y-6 rounded-2xl bg-neutral-50 p-8">
								<div className="flex items-center justify-between border-b border-neutral-200 pb-4">
									<span className="text-sm font-medium text-neutral-900">Tu estilo de redacción</span>
									<span className="rounded-full bg-neutral-200 px-3 py-1 text-xs text-neutral-600">Aprendido</span>
								</div>

								<div className="space-y-3">
									{['Demanda laboral.docx', 'Contrato de servicios.docx', 'Poder especial.docx'].map(doc => (
										<div key={doc} className="flex items-center gap-3 rounded-xl bg-white p-3">
											<FileText strokeWidth={1.5} className="h-4 w-4 shrink-0 text-neutral-400" />
											<span className="flex-1 text-sm text-neutral-700">{doc}</span>
											<CheckCircle2 strokeWidth={1.5} className="h-4 w-4 shrink-0 text-[#c9a962]" />
										</div>
									))}
								</div>

								<div className="rounded-xl bg-white p-5">
									<p className="mb-2 text-xs text-neutral-500">Documento nuevo</p>
									<p className="text-sm leading-relaxed text-neutral-800">
										Generado con tu estructura, tono y formato habituales, listo para revisar.
									</p>
								</div>
							</div>
						</div>
					</div>
				</ScrollReveal>

				{/* ROI Section */}
				<ScrollReveal className="rounded-3xl bg-neutral-950 p-10 text-white lg:p-14">
					<div className="grid items-center gap-12 lg:grid-cols-2">
						<div className="space-y-8">
							<Title component="h3" className="text-3xl text-white md:text-4xl">
								Impacto real en tu práctica
							</Title>

							<div className="space-y-4">
								<div className="flex items-center justify-between border-b border-neutral-800 pb-3">
									<span className="text-neutral-400">Plan Básico</span>
									<span>18 documentos/mes</span>
								</div>
								<div className="flex items-center justify-between border-b border-neutral-800 pb-3">
									<span className="text-neutral-400">Ahorro en horas</span>
									<span>~72 horas/mes</span>
								</div>
								<div className="flex items-center justify-between">
									<span className="text-neutral-400">Tu inversión</span>
									<span className="text-xl font-semibold">$29.95/mes</span>
								</div>
							</div>
						</div>

						<div className="flex flex-col items-center justify-center space-y-6 rounded-3xl border border-neutral-800 bg-neutral-900/40 p-10 text-center">
							<p className="text-sm font-medium uppercase tracking-widest text-neutral-500">Retorno de inversión</p>
							<Title component="span" className="text-6xl text-[#c9a962] md:text-7xl">
								8,400%
							</Title>
							<a
								href="/docs/Recurso-Casacion.docx"
								target="_blank"
								rel="noreferrer"
								download="Ejemplo Recurso de Casacion.docx"
								className="mt-4 flex items-center gap-2 rounded-full bg-white px-6 py-3 font-medium text-neutral-900 transition-colors hover:bg-neutral-100"
							>
								<Icon name="landing-download" className="h-4 w-4" />
								Ver ejemplo de documento
							</a>
						</div>
					</div>
				</ScrollReveal>
			</div>
		</section>
	)
}

function GainItem({
	icon: IconCmp,
	title,
	description,
	dark = false,
}: {
	icon: React.ElementType
	title: string
	description: string
	dark?: boolean
}) {
	return (
		<div className="flex gap-5">
			<div
				className={cn(
					'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border',
					dark ? 'border-neutral-800 bg-neutral-900 text-neutral-300' : 'border-neutral-200 bg-white text-neutral-500',
				)}
			>
				<IconCmp strokeWidth={1.5} className="h-5 w-5" />
			</div>
			<div>
				<h3 className={cn('text-lg font-medium tracking-tight', dark ? 'text-white' : 'text-neutral-950')}>{title}</h3>
				<p className={cn('mt-1.5 text-[15px] leading-relaxed', dark ? 'text-neutral-400' : 'text-neutral-500')}>
					{description}
				</p>
			</div>
		</div>
	)
}

function ImageFeature({
	src,
	alt,
	eyebrow,
	title,
	children,
	imageSide = 'right',
	dark = false,
}: {
	src: string
	alt: string
	eyebrow: string
	title: string
	children: React.ReactNode
	imageSide?: 'left' | 'right'
	dark?: boolean
}) {
	return (
		<div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
			<ScrollReveal className={imageSide === 'left' ? 'lg:order-1' : 'lg:order-2'}>
				<img
					src={src}
					alt={alt}
					width={1440}
					height={1080}
					loading="lazy"
					className="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl shadow-neutral-950/10 ring-1 ring-black/5"
				/>
			</ScrollReveal>
			<ScrollReveal
				className={cn('flex flex-col justify-center', imageSide === 'left' ? 'lg:order-2' : 'lg:order-1')}
				delay={100}
			>
				<p className="text-sm font-medium uppercase tracking-[0.2em] text-[#c9a962]">{eyebrow}</p>
				<Title component="h3" className={cn('mt-5 text-3xl md:text-4xl', dark && 'text-white')}>
					{title}
				</Title>
				<div
					className={cn(
						'mt-6 space-y-5 text-[17px] leading-snug tracking-tight',
						dark ? 'text-neutral-400' : 'text-neutral-600',
					)}
				>
					{children}
				</div>
			</ScrollReveal>
		</div>
	)
}

function IndividualLawyersSection() {
	return (
		<section className="w-full bg-neutral-50 py-32 md:py-40">
			<div className="mx-auto max-w-7xl px-6 lg:px-8">
				<div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
					<ScrollReveal className="space-y-6 lg:sticky lg:top-28 lg:self-start">
						<p className="text-sm font-medium uppercase tracking-widest text-[#c9a962]">Para abogados individuales</p>
						<Title component="h2" className="text-4xl md:text-5xl">
							Menos horas en lo repetitivo. Más tiempo en el derecho que importa.
						</Title>
						<p className="text-lg leading-relaxed text-neutral-600">
							Investigar desde cero y redactar los mismos documentos una y otra vez consume su día. IUS PRO se encarga
							de ese trabajo pesado y, además, le ayuda a elevar la calidad de lo que entrega.
						</p>
						<div className="pt-2">
							<MarketingButton variant="primary" size="lg" className="rounded-none" asChild>
								<Link prefetch="render" to="/signup">
									Iniciar prueba gratis
								</Link>
							</MarketingButton>
						</div>
					</ScrollReveal>

					<ScrollReveal className="space-y-10" delay={100}>
						<GainItem
							icon={Search}
							title="Investigación jurídica en minutos"
							description="Respuestas con citas verificables sobre más de 1.000.000 de sentencias y la normativa vigente, incluyendo La Gaceta, el Boletín Judicial y resoluciones de la Contraloría General de la República y del Tribunal Registral Administrativo. Deje de buscar a mano."
						/>
						<GainItem
							icon={FileText}
							title="Adiós al documento en blanco"
							description="Poderes, contratos y escritos rutinarios redactados en su estilo en minutos. Lo repetitivo deja de robarle horas."
						/>
						<GainItem
							icon={PenLine}
							title="Redacte más, y desde la ley"
							description="Genere también los documentos más complejos, como demandas y contrademandas, fundamentados directamente en los códigos vigentes y con las citas exactas de su caso."
						/>
					</ScrollReveal>
				</div>

				<ScrollReveal className="mt-24 overflow-hidden rounded-[2rem] bg-neutral-950 text-white">
					<div className="grid lg:grid-cols-12">
						<div className="relative min-h-[28rem] overflow-hidden lg:col-span-7 lg:min-h-[38rem]">
							<img
								src="/img/marketing/drafting.webp"
								alt="Primer plano de una pluma fuente de punta dorada sobre papel de algodón"
								width={1440}
								height={1080}
								loading="lazy"
								className="absolute inset-0 h-full w-full object-cover"
							/>

							<div className="absolute left-6 top-6 flex items-center gap-3 rounded-full bg-neutral-950/85 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.16em] text-neutral-200 backdrop-blur-sm sm:left-8 sm:top-8">
								<PenLine aria-hidden="true" strokeWidth={1.5} className="h-3.5 w-3.5 text-[#d6b96f]" />
								Redacción en su estilo
							</div>

							<div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-white/70 bg-[#fbfaf7]/95 p-5 text-neutral-950 shadow-[0_24px_60px_-32px_rgba(0,0,0,0.65)] backdrop-blur-sm sm:bottom-8 sm:left-auto sm:right-8 sm:w-[22rem] sm:p-6">
								<div className="flex items-start justify-between gap-4">
									<div>
										<p className="text-[10px] font-medium uppercase tracking-[0.18em] text-neutral-400">
											Borrador generado
										</p>
										<p className="mt-2 text-sm font-medium">Contestación de demanda</p>
									</div>
									<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f1ead9] text-[#8b7338]">
										<FileText aria-hidden="true" strokeWidth={1.5} className="h-4 w-4" />
									</span>
								</div>
								<div className="mt-5 space-y-2" aria-hidden="true">
									<div className="h-px w-full bg-neutral-200" />
									<div className="h-px w-4/5 bg-neutral-200" />
									<div className="h-px w-2/3 bg-neutral-200" />
								</div>
								<div className="mt-5 flex items-center gap-2 border-t border-neutral-200 pt-4 text-xs text-neutral-500">
									<CheckCircle2 aria-hidden="true" strokeWidth={1.6} className="h-4 w-4 text-[#9b803e]" />
									Estructura, argumentos y citas incluidos
								</div>
							</div>
						</div>

						<div className="flex flex-col justify-between p-8 sm:p-10 lg:col-span-5 lg:p-12 xl:p-14">
							<div>
								<div className="flex items-center justify-between gap-6">
									<p className="text-xs font-medium uppercase tracking-[0.2em] text-[#d6b96f]">Redacción inteligente</p>
									<span className="font-mono text-xs text-neutral-600">02</span>
								</div>
								<Title component="h3" className="mt-7 text-3xl text-white md:text-4xl">
									Del documento en blanco al borrador, en minutos
								</Title>
								<p className="mt-6 text-[16px] leading-relaxed text-neutral-400">
									Describa lo que necesita y reciba un escrito con la estructura correcta, los argumentos de su caso y
									las citas exactas, redactado en su propio estilo. Lo que antes tomaba horas, ahora es revisar y
									firmar.
								</p>
							</div>

							<ol className="mt-12 divide-y divide-neutral-800 border-t border-neutral-800">
								{[
									['01', 'Describa lo que necesita'],
									['02', 'Reciba un borrador fundamentado'],
									['03', 'Revise, ajuste y firme'],
								].map(([number, label]) => (
									<li key={number} className="flex items-center gap-5 py-4">
										<span className="font-mono text-[11px] text-[#d6b96f]">{number}</span>
										<span className="text-sm text-neutral-300">{label}</span>
									</li>
								))}
							</ol>
						</div>
					</div>
				</ScrollReveal>

				{/* Expediente highlight */}
				<ScrollReveal className="mt-20 rounded-3xl border border-neutral-200 bg-white">
					<div className="grid gap-10 p-10 lg:grid-cols-[1.3fr_1fr] lg:gap-16 lg:p-14">
						<div className="space-y-5">
							<span className="inline-block font-mono text-xs uppercase tracking-widest text-[#c9a962]">
								Una función insignia
							</span>
							<Title component="h3" className="text-3xl md:text-4xl">
								¿Trabaja expedientes? El análisis va más allá del tiempo.
							</Title>
							<p className="text-base leading-relaxed text-neutral-600">
								Suba el expediente completo y reciba un análisis profundo que no solo ahorra horas: mejora la calidad de
								su servicio. Encuentra cronología, riesgos, fortalezas y jurisprudencia relacionada que quizá no habría
								encontrado, e integra ese análisis con el chat y la herramienta de redacción para preguntar o redactar
								con todo el contexto del caso.
							</p>
						</div>
						<ul className="flex flex-col justify-center gap-4 border-t border-neutral-100 pt-8 text-sm text-neutral-600 lg:border-l lg:border-t-0 lg:pl-16 lg:pt-0">
							<li className="flex gap-3">
								<span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#c9a962]" />
								Cronología, hechos, riesgos y fortalezas del caso
							</li>
							<li className="flex gap-3">
								<span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#c9a962]" />
								Jurisprudencia relacionada y patrones de resolución
							</li>
							<li className="flex gap-3">
								<span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#c9a962]" />
								Integrado con el chat y la herramienta de redacción del mismo expediente
							</li>
						</ul>
					</div>
				</ScrollReveal>

				{/* Administrative / everyday balancing note */}
				<ScrollReveal className="mt-8">
					<p className="mx-auto max-w-2xl text-center text-sm leading-relaxed text-neutral-400">
						¿Su día es más de gestión y trámite que de litigio? IUS PRO también es su asistente para las consultas, la
						redacción y la revisión del trabajo administrativo de todos los días.
					</p>
				</ScrollReveal>
			</div>
		</section>
	)
}

const firmCapabilities = [
	{
		icon: Briefcase,
		title: 'Debida diligencia',
		description: 'Revise miles de contratos y documentos soporte en horas.',
	},
	{
		icon: FileText,
		title: 'Revisión de contratos',
		description: 'Detecte riesgos, incumplimientos y cláusulas críticas automáticamente.',
	},
	{
		icon: ShieldCheck,
		title: 'Cumplimiento',
		description: 'Compare documentos contra normativa vigente y políticas internas.',
	},
	{
		icon: Gavel,
		title: 'Inteligencia para litigios',
		description:
			'Analice expedientes completos y obtenga estrategia procesal respaldada por jurisprudencia costarricense.',
	},
	{
		icon: Workflow,
		title: 'Gestión del conocimiento',
		description: 'Convierta el conocimiento de su firma en agentes reutilizables.',
	},
]

function FirmsSection() {
	return (
		<section className="w-full bg-neutral-950 py-32 text-white md:py-40">
			<div className="mx-auto max-w-7xl px-6 lg:px-8">
				<div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
					<ScrollReveal className="space-y-6 lg:sticky lg:top-28 lg:self-start">
						<p className="text-sm font-medium uppercase tracking-widest text-[#c9a962]">
							Para bufetes y equipos legales
						</p>
						<Title component="h2" className="text-4xl text-white md:text-5xl">
							Todo lo anterior. Más un espacio donde el equipo trabaja unido.
						</Title>
						<p className="text-lg leading-relaxed text-neutral-400">
							Su equipo obtiene las mismas ganancias que el abogado individual en investigación, redacción y análisis.
							Además, IUS PRO se convierte en el lugar donde el trabajo legal del despacho sucede en conjunto.
						</p>
						<div className="flex flex-col gap-4 pt-2 sm:flex-row">
							<MarketingButton variant="inverted" className="px-8" asChild>
								<Link prefetch="render" to="/links/demo" target="_blank">
									Agendar demo
								</Link>
							</MarketingButton>
							<MarketingButton variant="inverted-outline" className="px-8" asChild>
								<Link prefetch="intent" to="/despachos">
									IUS PRO para despachos
								</Link>
							</MarketingButton>
						</div>
					</ScrollReveal>

					<ScrollReveal className="space-y-10" delay={100}>
						<GainItem
							dark
							icon={Users}
							title="Un espacio de trabajo compartido"
							description="Clientes, expedientes, chats y documentos en un solo lugar, accesibles para quien corresponde dentro del equipo."
						/>
						<GainItem
							dark
							icon={Layers}
							title="Revisión a gran escala"
							description="Extraiga datos y revise cientos de documentos a la vez con tablas de revisión y playbooks: debida diligencia y auditorías que antes tomaban semanas."
						/>
						<GainItem
							dark
							icon={Share2}
							title="Un mismo criterio para todo el equipo"
							description="Compartan instrucciones, agentes y plantillas de revisión para que el trabajo salga consistente, sin importar quién lo haga."
						/>
					</ScrollReveal>
				</div>

				<div className="mt-24">
					<ImageFeature
						src="/img/marketing/studio.webp"
						alt="Sala de estar de un bufete moderno al atardecer, con biblioteca y vista a la ciudad"
						eyebrow="Para bufetes y equipos"
						title="El conocimiento de su firma, siempre disponible"
						imageSide="right"
						dark
					>
						<p>
							Los criterios y la forma de trabajar de su despacho dejan de vivir en la cabeza de unos pocos. IUS PRO los
							convierte en agentes y plantillas reutilizables, para que cada abogado entregue con el mejor estándar de
							la casa.
						</p>
					</ImageFeature>
				</div>

				{/* Diseñado para firmas legales modernas: capability grid */}
				<ScrollReveal className="mt-24 border-t border-neutral-800 pt-20">
					<Title component="h2" className="max-w-2xl text-3xl text-white md:text-4xl">
						Diseñado para firmas legales modernas
					</Title>
				</ScrollReveal>

				<div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{firmCapabilities.map((cap, i) => (
						<ScrollReveal
							key={cap.title}
							className="flex flex-col rounded-2xl border border-neutral-800 bg-neutral-900/40 p-8 lg:p-10"
							delay={(i % 3) * 80}
						>
							<cap.icon strokeWidth={1.5} className="h-6 w-6 text-[#c9a962]" />
							<h3 className="mt-6 text-lg font-medium tracking-tight text-white">{cap.title}</h3>
							<p className="mt-2 flex-1 text-[15px] leading-relaxed text-neutral-400">{cap.description}</p>
						</ScrollReveal>
					))}
				</div>
			</div>
		</section>
	)
}

function SecuritySection() {
	return (
		<section className="w-full bg-white py-32 md:py-40" id="comparacion">
			<div className="mx-auto max-w-7xl space-y-24 px-6 lg:px-8">
				{/* Header */}
				<ScrollReveal className="max-w-3xl space-y-6">
					<Title component="h2" className="text-4xl md:text-5xl">
						Seguridad de Nivel Bancario
					</Title>
					<p className="text-xl leading-relaxed tracking-tight text-neutral-600">
						Tus documentos legales están protegidos con la misma tecnología que utilizan los bancos más grandes del
						mundo.
					</p>
					<Link
						prefetch="intent"
						to="/producto/seguridad"
						className="inline-flex text-sm font-medium text-neutral-900 underline underline-offset-4 transition-colors hover:text-[#8b7338]"
					>
						Ver detalles de seguridad de IUS PRO
					</Link>
				</ScrollReveal>

				{/* Cards Grid */}
				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
					<ScrollReveal className="rounded-3xl bg-neutral-50 p-10">
						<div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-pink-50 text-pink-600">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
								className="h-5 w-5"
							>
								<ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
								<path d="M3 5V19A9 3 0 0 0 21 19V5"></path>
								<path d="M3 12A9 3 0 0 0 21 12"></path>
							</svg>
						</div>
						<p className="mb-2 text-lg font-medium text-neutral-950">Almacenamiento Amazon S3</p>
						<p className="leading-relaxed tracking-tight text-neutral-600">
							Infraestructura utilizada por millones de empresas. 99.999999999% de durabilidad.
						</p>
					</ScrollReveal>

					<ScrollReveal className="rounded-3xl bg-neutral-50 p-10" delay={100}>
						<div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
								className="h-5 w-5"
							>
								<rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
								<path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
							</svg>
						</div>
						<p className="mb-2 text-lg font-medium text-neutral-950">Cifrado en tránsito y reposo</p>
						<p className="leading-relaxed tracking-tight text-neutral-600">
							Protección de documentos y datos sensibles durante almacenamiento, carga y consulta.
						</p>
					</ScrollReveal>

					<ScrollReveal className="rounded-3xl bg-neutral-50 p-10" delay={200}>
						<div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-50 text-purple-600">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
								className="h-5 w-5"
							>
								<path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
								<path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
								<path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
								<line x1="2" x2="22" y1="2" y2="22"></line>
							</svg>
						</div>
						<p className="mb-2 text-lg font-medium text-neutral-950">Controles de acceso</p>
						<p className="leading-relaxed tracking-tight text-neutral-600">
							Acceso restringido por usuario y espacio de trabajo para proteger documentos, expedientes y conocimiento
							interno.
						</p>
					</ScrollReveal>

					<ScrollReveal className="rounded-3xl bg-neutral-50 p-10" delay={300}>
						<div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-600">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
								className="h-5 w-5"
							>
								<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
								<path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
								<path d="m9 15 2 2 4-4"></path>
							</svg>
						</div>
						<p className="mb-2 text-lg font-medium text-neutral-950">Control para su práctica legal</p>
						<p className="leading-relaxed tracking-tight text-neutral-600">
							Infraestructura, registro de actividad y controles diseñados para manejar información legal confidencial.
						</p>
					</ScrollReveal>

					<ScrollReveal className="rounded-3xl bg-neutral-50 p-10" delay={400}>
						<div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
								className="h-5 w-5"
							>
								<path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
							</svg>
						</div>
						<p className="mb-2 text-lg font-medium text-neutral-950">Monitoreo 24/7</p>
						<p className="leading-relaxed tracking-tight text-neutral-600">
							Detección y respuesta automática ante amenazas en tiempo real.
						</p>
					</ScrollReveal>

					<ScrollReveal className="rounded-3xl bg-neutral-50 p-10" delay={500}>
						<div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
								className="h-5 w-5"
							>
								<path d="M3 7V5a2 2 0 0 1 2-2h2"></path>
								<path d="M17 3h2a2 2 0 0 1 2 2v2"></path>
								<path d="M21 17v2a2 2 0 0 1-2 2h-2"></path>
								<path d="M7 21H5a2 2 0 0 1-2-2v-2"></path>
							</svg>
						</div>
						<p className="mb-2 text-lg font-medium text-neutral-950">Protección Anti-Malware</p>
						<p className="leading-relaxed tracking-tight text-neutral-600">
							Archivos escaneados automáticamente con múltiples motores de detección.
						</p>
					</ScrollReveal>
				</div>

				{/* Stats */}
				<ScrollReveal className="grid grid-cols-2 gap-8 border-t border-neutral-200 pt-12 md:grid-cols-4">
					<div>
						<p className="text-2xl font-semibold text-neutral-900">256-bit</p>
						<p className="mt-1 text-sm tracking-tight text-neutral-500">Cifrado Militar</p>
					</div>
					<div>
						<p className="text-2xl font-semibold text-neutral-900">99.99%</p>
						<p className="mt-1 text-sm tracking-tight text-neutral-500">Tiempo Activo</p>
					</div>
					<div>
						<p className="text-2xl font-semibold text-neutral-900">24/7</p>
						<p className="mt-1 text-sm tracking-tight text-neutral-500">Monitoreo</p>
					</div>
					<div>
						<p className="text-2xl font-semibold text-neutral-900">0</p>
						<p className="mt-1 text-sm tracking-tight text-neutral-500">Brechas de Seguridad</p>
					</div>
				</ScrollReveal>
			</div>
		</section>
	)
}
